import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Eye, Download, Edit, Loader2, CheckCircle, X, Save, Calendar, User, FileText, Image, Upload, Search, Undo2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useTransacoesFinanceirasPaginated,
  useTransacoesFinanceirasCount,
  useAtualizarTransacaoFinanceira,
  useExcluirTransacaoFinanceira,
  useContasAReceberPaginated,
  fetchTransacoesFinanceirasFiltradas,
  type FinanceiroFiltroParams,
} from "@/hooks/useFinanceiro";
import {
  useMarcarParcelaComoPaga,
  useAtualizarParcela,
  useExcluirParcela,
  type Parcela,
} from "@/hooks/useParcelas";
import {
  type FinanceiroLinha,
  type FinanceiroSort,
  type FinanceiroSortField,
  mergeContasAReceber,
  linhaRecebivelKey,
} from "@/lib/financeiroRecebiveis";
import { parseDateLocal } from "@/lib/datas";
import { openStorageUrl, uploadFileToStorage } from "@/lib/storageUrl";
import { useFormasPagamentoFinanceiro } from "@/hooks/useFinanceiro";
import { useFormasPagamentoAtivas } from "@/hooks/useFormasPagamento";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useEffect, useMemo, type ChangeEvent, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { NovaDespesaDialog } from "./NovaDespesaDialog";
import { AprovarComComprovanteDialog } from "./AprovarComComprovanteDialog";
import { Plus } from "lucide-react";
import { ListagemAcoes, type ListagemColumn } from "@/components/listagem/ListagemAcoes";
import { BRASIL_UFS } from "@/lib/estados";
import { useCidadesPorEstado } from "@/hooks/useClientesEstadosCidades";
import { useAuth } from "@/hooks/useAuth";
import { pedidoEstaAguardandoConfirmacao } from "@/hooks/usePedidos";
import {
  buildFinanceiroFiltroParams,
  getFinanceiroFiltrosDraftDefault,
  type FinanceiroFiltrosDraft,
} from "@/lib/financeiroFiltros";
import {
  parseNotasFiscais,
  syncLegacyNotaFiscalFields,
  temNotaFiscal,
  type NotaFiscalAnexo,
} from "@/lib/transacaoNotasFiscais";

interface FinanceiroTableProps {
  /** Filtro por tipo: só entradas (receber), só saídas (pagar) ou todos. Não altera comportamento se omitido. */
  filtroTipo?: 'entrada' | 'saida' | null;
  filtrosDraft?: FinanceiroFiltrosDraft;
  setFiltrosDraft?: Dispatch<SetStateAction<FinanceiroFiltrosDraft>>;
  filtrosAplicados?: FinanceiroFiltrosDraft;
  setFiltrosAplicados?: Dispatch<SetStateAction<FinanceiroFiltrosDraft>>;
  filtrosDraftDefault?: FinanceiroFiltrosDraft;
  /** Se true: só listagem (sem marcar pago / editar / excluir / nova despesa) */
  somenteLeitura?: boolean;
}

function getPedidoLabel(linha: FinanceiroLinha): string {
  if (linha.pedido_id) return `#${linha.pedido_id}`;
  if (linha.pedido?.id) return `#${linha.pedido.id}`;
  return "—";
}

export function FinanceiroTable({
  filtroTipo = null,
  filtrosDraft: filtrosDraftProp,
  setFiltrosDraft: setFiltrosDraftProp,
  filtrosAplicados: filtrosAplicadosProp,
  setFiltrosAplicados: setFiltrosAplicadosProp,
  filtrosDraftDefault: filtrosDraftDefaultProp,
  somenteLeitura = false,
}: FinanceiroTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [sort, setSort] = useState<FinanceiroSort | null>(null);
  const [buscaTransacaoInput, setBuscaTransacaoInput] = useState("");
  const [buscaTransacao, setBuscaTransacao] = useState("");
  const [showNovaDespesa, setShowNovaDespesa] = useState(false);
  const filtrosDraftDefaultLocal = useMemo(() => getFinanceiroFiltrosDraftDefault(), []);
  const filtrosDraftDefault = filtrosDraftDefaultProp ?? filtrosDraftDefaultLocal;
  const [filtrosDraftLocal, setFiltrosDraftLocal] = useState(filtrosDraftDefault);
  const [filtrosAplicadosLocal, setFiltrosAplicadosLocal] = useState(filtrosDraftDefault);
  const filtrosDraft = filtrosDraftProp ?? filtrosDraftLocal;
  const setFiltrosDraft = setFiltrosDraftProp ?? setFiltrosDraftLocal;
  const filtrosAplicados = filtrosAplicadosProp ?? filtrosAplicadosLocal;
  const setFiltrosAplicados = setFiltrosAplicadosProp ?? setFiltrosAplicadosLocal;
  const { data: cidadesDoEstado = [] } = useCidadesPorEstado(filtrosDraft.estado);

  const filtrosHook = useMemo<FinanceiroFiltroParams>(
    () => buildFinanceiroFiltroParams(filtrosAplicados),
    [filtrosAplicados]
  );
  
  useEffect(() => {
    if (buscaTransacaoInput === "") {
      setBuscaTransacao("");
      return;
    }
    const timer = setTimeout(() => setBuscaTransacao(buscaTransacaoInput), 700);
    return () => clearTimeout(timer);
  }, [buscaTransacaoInput]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [buscaTransacao, filtroTipo, filtrosHook, sort?.field, sort?.direction]);

  const toggleSort = (field: FinanceiroSortField) => {
    setSort((prev) => {
      if (prev?.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "asc" };
    });
  };

  const sortIndicator = (field: FinanceiroSortField) =>
    sort?.field === field ? (sort.direction === "asc" ? " ▲" : " ▼") : "";

  const isReceber = filtroTipo === "entrada";

  const { data: receberResult, isLoading: loadingReceber, error: errorReceber } =
    useContasAReceberPaginated(
      currentPage,
      itemsPerPage,
      buscaTransacao,
      filtrosHook,
      isReceber,
      sort
    );
  const { data: transacoesResult, isLoading: loadingTransacoes, error: errorTransacoes } =
    useTransacoesFinanceirasPaginated(
      currentPage,
      itemsPerPage,
      buscaTransacao,
      filtroTipo ?? undefined,
      filtrosHook,
      filtroTipo === "saida",
      sort
    );

  const linhas: FinanceiroLinha[] = isReceber
    ? ((receberResult?.data ?? []) as FinanceiroLinha[])
    : ((transacoesResult?.data ?? []) as unknown as FinanceiroLinha[]);
  const totalLinhas = isReceber
    ? (receberResult?.count || 0)
    : (transacoesResult?.count || 0);
  const isLoading = isReceber ? loadingReceber : loadingTransacoes;
  const error = isReceber ? errorReceber : errorTransacoes;
  const atualizarTransacao = useAtualizarTransacaoFinanceira();
  const excluirTransacao = useExcluirTransacaoFinanceira();
  const excluirParcela = useExcluirParcela();
  const marcarParcelaComoPaga = useMarcarParcelaComoPaga();
  const atualizarParcela = useAtualizarParcela();
  const { canDelete } = useAuth();
  const { data: formasPagamentoDb = [] } = useFormasPagamentoFinanceiro();
  const { data: formasPagamentoAtivas = [] } = useFormasPagamentoAtivas();

  const formaPagamentoLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const fp of formasPagamentoAtivas as any[]) {
      if (fp?.id != null && fp?.nome) map.set(String(fp.id), String(fp.nome));
    }
    return (raw: unknown) => {
      const v = raw == null ? "" : String(raw).trim();
      if (!v) return "—";
      return map.get(v) ?? v;
    };
  }, [formasPagamentoAtivas]);
  
  // Estados para os modais
  const [showVisualizarModal, setShowVisualizarModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showEstornoModal, setShowEstornoModal] = useState(false);
  const [transacaoParaEstornar, setTransacaoParaEstornar] = useState<any>(null);
  const [transacaoAprovarComComprovante, setTransacaoAprovarComComprovante] = useState<any>(null);
  const [parcelaAprovarComComprovante, setParcelaAprovarComComprovante] = useState<Parcela | null>(null);
  const [parcelaParaEstornar, setParcelaParaEstornar] = useState<Parcela | null>(null);
  const [transacaoSelecionada, setTransacaoSelecionada] = useState<any>(null);
  const [observacoesVisualizacao, setObservacoesVisualizacao] = useState("");
  const [salvandoObservacoes, setSalvandoObservacoes] = useState(false);
  const [transacaoParaExcluir, setTransacaoParaExcluir] = useState<FinanceiroLinha | null>(null);
  const [parcelaParaExcluir, setParcelaParaExcluir] = useState<Parcela | null>(null);
  const [editandoTransacao, setEditandoTransacao] = useState<any>(null);
  const [notasFiscaisEdit, setNotasFiscaisEdit] = useState<NotaFiscalAnexo[]>([]);
  const [notasFiscaisNovas, setNotasFiscaisNovas] = useState<File[]>([]);
  const transacaoColumns: ListagemColumn<any>[] = [
    { key: "id", label: "ID", value: (t) => t.id },
    { key: "cliente", label: "Cliente/Fornecedor", value: (t) => t.fornecedor?.nome ?? t.cliente?.nome ?? (t.fornecedor_id ? `Fornecedor ${t.fornecedor_id}` : t.cliente_id ? `Cliente ${t.cliente_id}` : "—") },
    { key: "pedido", label: "Pedido", value: (t) => getPedidoLabel(t), toggleable: filtroTipo === "entrada" },
    { key: "parcela", label: "Parcela", value: (t) => getParcelaLabel(t) ?? "—", toggleable: filtroTipo === "entrada" },
    { key: "tipo", label: "Tipo", value: (t) => t.tipo },
    { key: "valor", label: "Valor", value: (t) => formatarValor(t.valor) },
    { key: "forma", label: "Forma Pagamento", value: (t) => formaPagamentoLabel(t.forma_pagamento) },
    { key: "status", label: "Status", value: (t) => (isTransacaoVencida(t) ? "vencido" : t.status) },
    { key: "vencimento", label: "Vencimento", value: (t) => t.data_vencimento ?? "" },
    { key: "pagamento", label: "Pagamento", value: (t) => t.data_pagamento ?? "" },
  ];
  const defaultVisible = useMemo(() => {
    const base = ["id", "cliente", "tipo", "valor", "forma", "status", "vencimento", "pagamento"];
    if (filtroTipo === "entrada") base.splice(2, 0, "pedido", "parcela");
    return base;
  }, [filtroTipo]);
  const [visibleTransCols, setVisibleTransCols] = useState<string[]>(defaultVisible);
  useEffect(() => {
    setVisibleTransCols(defaultVisible);
  }, [defaultVisible]);
  const toggleTransCol = (key: string, nextVisible: boolean) => {
    setVisibleTransCols((prev) => (nextVisible ? Array.from(new Set([...prev, key])) : prev.filter((k) => k !== key)));
  };

  const isTransacaoVencida = (transacao: FinanceiroLinha) => {
    if (transacao.status !== 'pendente' || !transacao.data_vencimento) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const vencimento = parseDateLocal(transacao.data_vencimento);
    return vencimento < hoje;
  };

  const formatarDataLocal = (data?: string | null) => {
    if (!data) return "-";
    return parseDateLocal(data).toLocaleDateString("pt-BR");
  };

  const uploadComprovanteParcela = async (file: File, parcelaId: number) => {
    const isPdf = file.type === "application/pdf";
    const isImage = file.type.startsWith("image/");
    if (!isPdf && !isImage) {
      throw new Error("Envie um PDF ou imagem (JPEG, PNG, WebP, GIF).");
    }
    const timestamp = Date.now();
    const fileName = `comprovante_${parcelaId}_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, "_")}`;
    const filePath = `comprovantes_parcelas/${parcelaId}/${fileName}`;
    const { publicUrl } = await uploadFileToStorage(filePath, file);
    return { comprovante_pagamento_url: publicUrl, comprovante_pagamento_name: file.name };
  };

  const estornarParcela = async (parcela: Parcela) => {
    try {
      await atualizarParcela.mutateAsync({
        id: parcela.id,
        status: "pendente",
        data_pagamento: null,
        comprovante_pagamento_url: null,
        comprovante_pagamento_name: null,
      });
      toast.success("Parcela estornada. Status: pendente.");
      setShowEstornoModal(false);
      setParcelaParaEstornar(null);
    } catch (err) {
      console.error("Erro ao estornar parcela:", err);
      toast.error("Erro ao estornar parcela");
    }
  };

  const estornarTransacao = async (transacao: any) => {
    try {
      await atualizarTransacao.mutateAsync({
        id: transacao.id,
        status: 'pendente',
        data_pagamento: null,
      });
      const isSaida = transacao.tipo === 'saida';
      toast.success(isSaida ? 'Despesa estornada. Status: pendente.' : 'Recebimento estornado. Status: pendente.');
      setShowEstornoModal(false);
      setTransacaoParaEstornar(null);
    } catch (error) {
      console.error('Erro ao estornar:', error);
      toast.error('Erro ao estornar');
    }
  };

  const marcarComoPagoOuRecebido = async (transacao: any) => {
    try {
      const dataPagamento = new Date().toISOString().split('T')[0];
      await atualizarTransacao.mutateAsync({
        id: transacao.id,
        status: 'pago',
        data_pagamento: dataPagamento
      });
      const isSaida = transacao.tipo === 'saida';
      toast.success(isSaida ? 'Despesa marcada como paga!' : 'Transação marcada como recebida!');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const isObservacaoAutomatica = (obs?: string | null) =>
    !!obs?.startsWith("Pedido criado automaticamente após aprovação do orçamento");

  const visualizarTransacao = (transacao: any) => {
    setTransacaoSelecionada(transacao);
    setObservacoesVisualizacao(
      isObservacaoAutomatica(transacao.observacoes) ? "" : (transacao.observacoes || "")
    );
    setShowVisualizarModal(true);
  };

  const salvarObservacoesVisualizacao = async () => {
    if (!transacaoSelecionada?.id) return;
    const observacoes = observacoesVisualizacao.trim() || null;
    setSalvandoObservacoes(true);
    try {
      if (transacaoSelecionada._origem === "parcela") {
        await atualizarParcela.mutateAsync({
          id: Number(transacaoSelecionada.id),
          observacoes: observacoes ?? "",
        });
      } else {
        await atualizarTransacao.mutateAsync({
          id: Number(transacaoSelecionada.id),
          observacoes: observacoes ?? "",
        });
      }
      setTransacaoSelecionada({
        ...transacaoSelecionada,
        observacoes: observacoes ?? "",
      });
      toast.success("Observações salvas");
    } catch (error) {
      console.error("Erro ao salvar observações:", error);
      toast.error("Erro ao salvar observações");
    } finally {
      setSalvandoObservacoes(false);
    }
  };

  const baixarNotaFiscal = async (transacao: any) => {
    const notas = parseNotasFiscais(transacao);
    if (notas.length === 0) {
      toast.error('Nenhum comprovante anexado a esta transação');
      return;
    }
    await Promise.all(notas.map((nota) => openStorageUrl(nota.url)));
    toast.success(
      notas.length === 1
        ? 'Comprovante aberto em nova aba!'
        : `${notas.length} comprovantes abertos em novas abas!`
    );
  };

  const fecharModalEdicao = () => {
    setShowEditarModal(false);
    setEditandoTransacao(null);
    setNotasFiscaisEdit([]);
    setNotasFiscaisNovas([]);
  };

  const editarTransacao = (transacao: any) => {
    setEditandoTransacao({
      ...transacao,
      data_vencimento: transacao.data_vencimento ? new Date(transacao.data_vencimento).toISOString().split('T')[0] : '',
      data_pagamento: transacao.data_pagamento ? new Date(transacao.data_pagamento).toISOString().split('T')[0] : ''
    });
    setNotasFiscaisEdit(parseNotasFiscais(transacao));
    setNotasFiscaisNovas([]);
    setShowEditarModal(true);
  };

  const isArquivoAnexoPermitido = (file: File) => {
    const isPdf = file.type === 'application/pdf' || file.type.includes('pdf');
    const isImage = file.type.startsWith('image/');
    return isPdf || isImage;
  };

  const handleSelecionarNotasFiscais = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    const validos: File[] = [];
    for (const file of Array.from(files)) {
      if (!isArquivoAnexoPermitido(file)) {
        toast.error(`${file.name}: envie PDF ou imagem (JPEG, PNG, WebP, GIF)`);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name}: máximo 20MB`);
        continue;
      }
      validos.push(file);
    }

    if (validos.length > 0) {
      setNotasFiscaisNovas((prev) => [...prev, ...validos]);
      toast.success(`${validos.length} arquivo(s) selecionado(s)`);
    }
    event.target.value = '';
  };

  const uploadNotaFiscal = async (file: File, transacaoId: number) => {
    try {
      if (!file) {
        throw new Error('Arquivo não fornecido');
      }

      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Máximo 20MB permitido.');
      }

      if (!isArquivoAnexoPermitido(file)) {
        throw new Error('Envie um PDF ou imagem (JPEG, PNG, WebP, GIF).');
      }

      return uploadArquivoTransacao(file, transacaoId, 'nota_fiscal');
    } catch (error) {
      console.error('Erro detalhado no upload do comprovante:', error);
      throw error;
    }
  };

  const uploadComprovante = async (file: File, transacaoId: number) => {
    if (!isArquivoAnexoPermitido(file)) {
      throw new Error('Envie um PDF ou imagem (JPEG, PNG, WebP, GIF).');
    }
    return uploadArquivoTransacao(file, transacaoId, 'comprovante');
  };

  const uploadArquivoTransacao = async (file: File, transacaoId: number, prefixo: string) => {
    const folder = prefixo === 'comprovante' ? 'comprovantes' : 'notas_fiscais';
    const timestamp = Date.now();
    const fileName = `${prefixo}_${transacaoId}_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const filePath = `${folder}/${transacaoId}/${fileName}`;
    const { publicUrl } = await uploadFileToStorage(filePath, file);
    return {
      url: publicUrl,
      nome: file.name,
      nota_fiscal_url: publicUrl,
      nota_fiscal_name: file.name,
    };
  };

  const salvarEdicao = async () => {
    if (!editandoTransacao) return;

    try {
      const updateData: any = {
        id: editandoTransacao.id,
        status: editandoTransacao.status,
        data_vencimento: editandoTransacao.data_vencimento || null,
        data_pagamento: editandoTransacao.data_pagamento || null,
        observacoes: editandoTransacao.observacoes || null
      };

      const novasUploadadas: NotaFiscalAnexo[] = [];
      for (const file of notasFiscaisNovas) {
        const uploaded = await uploadNotaFiscal(file, editandoTransacao.id);
        novasUploadadas.push({ url: uploaded.url, nome: uploaded.nome });
      }

      const todasNotas = [...notasFiscaisEdit, ...novasUploadadas];
      Object.assign(updateData, syncLegacyNotaFiscalFields(todasNotas));

      await atualizarTransacao.mutateAsync(updateData);

      toast.success('Transação atualizada com sucesso!');
      fecharModalEdicao();
    } catch (error) {
      console.error('Erro ao atualizar transação:', error);
      toast.error('Erro ao atualizar transação');
    }
  };

  const formatarData = (data: string) => formatarDataLocal(data);

  const formatarValor = (valor: number) => {
    return `R$ ${Number(valor).toFixed(2).replace('.', ',')}`;
  };

  if (isLoading) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="ml-2">Carregando transações...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-0 shadow-md">
        <CardContent className="p-8">
          <div className="text-center text-red-600">
            Erro ao carregar transações: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string, transacao?: any) => {
    if (transacao && isTransacaoVencida(transacao)) return 'bg-red-100 text-red-800';
    switch (status) {
      case 'pago': return 'bg-green-100 text-green-800';
      case 'pendente': return 'bg-yellow-100 text-yellow-800';
      case 'cancelado': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'entrada': return 'bg-green-100 text-green-800';
      case 'saida': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  /** Retorna "X/Y" (ex.: "1/4") quando a transação é parcela; senão null. */
  const getParcelaLabel = (t: { numero_parcela?: number; total_parcelas?: number; observacoes?: string }): string | null => {
    if (t.numero_parcela != null && t.total_parcelas != null && t.total_parcelas > 0) {
      return `${t.numero_parcela}/${t.total_parcelas}`;
    }
    if (t.observacoes) {
      const match = t.observacoes.match(/(?:Parcela\s*)?(\d+)\s*\/\s*(\d+)/i) || t.observacoes.match(/\((\d+)\/(\d+)\)/);
      if (match) return `${match[1]}/${match[2]}`;
    }
    return null;
  };

  return (
    <>
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex flex-wrap justify-between items-center gap-2">
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <DollarSign className="h-5 w-5 text-green-600" />
              {filtroTipo === 'saida'
                ? `Contas a pagar`
                : filtroTipo === 'entrada'
                  ? `Contas a receber`
                  : `Transações Financeiras`}
            </CardTitle>
            <div className="flex items-center gap-2">
              <ListagemAcoes
                title="Financeiro"
                subtitle={`${filtroTipo === "saida" ? "Contas a pagar" : filtroTipo === "entrada" ? "Contas a receber" : "Todas"} • Página ${currentPage}`}
                rows={linhas}
                columns={transacaoColumns.filter((c) => c.toggleable !== false)}
                visibleKeys={visibleTransCols}
                onToggleColumn={toggleTransCol}
                filenameBase={`financeiro_${filtroTipo ?? "todos"}_p${currentPage}`}
                enableExport
                fetchAllRows={async () => {
                  if (filtroTipo === "entrada") {
                    const transacoes = await fetchTransacoesFinanceirasFiltradas(filtrosHook, {
                      tipo: "entrada",
                      searchTerm: buscaTransacao,
                    });
                    return mergeContasAReceber([], transacoes);
                  }

                  const pageSize = 1000;
                  let offset = 0;
                  const all: any[] = [];

                  const clienteFilterPresent = !!(
                    (filtrosHook?.estado && filtrosHook.estado.trim()) ||
                    (filtrosHook?.cidade && filtrosHook.cidade.trim())
                  );

                  // eslint-disable-next-line no-constant-condition
                  while (true) {
                    let query = supabase
                      .from("transacoes_financeiras")
                      .select(
                        `
                        *,
                        cliente:clientes${clienteFilterPresent ? "!inner" : ""}(*),
                        fornecedor:fornecedores(id,nome,email,telefone,cnpj),
                        pedido:pedidos(*),
                        contrato:contratos!contrato_id(*)
                      `
                      )
                      .order("created_at", { ascending: false });

                    if (filtroTipo) query = query.eq("tipo", filtroTipo);

                    if (filtrosHook?.vencimentoPeriodo?.dataInicio) query = query.gte("data_vencimento", filtrosHook.vencimentoPeriodo.dataInicio);
                    if (filtrosHook?.vencimentoPeriodo?.dataFim) query = query.lte("data_vencimento", filtrosHook.vencimentoPeriodo.dataFim);
                    if (filtrosHook?.pagamentoPeriodo?.dataInicio) query = query.gte("data_pagamento", filtrosHook.pagamentoPeriodo.dataInicio);
                    if (filtrosHook?.pagamentoPeriodo?.dataFim) query = query.lte("data_pagamento", filtrosHook.pagamentoPeriodo.dataFim);
                    if (typeof filtrosHook?.valorMin === "number") query = query.gte("valor", filtrosHook.valorMin);
                    if (typeof filtrosHook?.valorMax === "number") query = query.lte("valor", filtrosHook.valorMax);
                    if (filtrosHook?.formaPagamento && filtrosHook.formaPagamento !== "todos") query = query.ilike("forma_pagamento", filtrosHook.formaPagamento);

                    if (filtrosHook?.parcelas === "parceladas") query = query.gt("total_parcelas", 1);
                    if (filtrosHook?.parcelas === "avista") query = query.or("total_parcelas.is.null,total_parcelas.lte.1");

                    if (filtrosHook?.estado) query = query.eq("cliente.estado", filtrosHook.estado.toUpperCase());
                    if (filtrosHook?.cidade) query = query.eq("cliente.cidade", filtrosHook.cidade);

                    if (filtrosHook?.status && filtrosHook.status !== "todos") {
                      if (filtrosHook.status === "vencido") {
                        const hoje = new Date();
                        hoje.setHours(0, 0, 0, 0);
                        const hojeStr = hoje.toISOString().split("T")[0];
                        query = query.eq("status", "pendente").lt("data_vencimento", hojeStr);
                      } else {
                        query = query.eq("status", filtrosHook.status);
                      }
                    }

                    if (buscaTransacao && buscaTransacao.trim()) {
                      const searchLower = buscaTransacao.trim().toLowerCase();
                      if (!isNaN(Number(buscaTransacao.trim()))) {
                        query = query.eq("id", parseInt(buscaTransacao.trim()));
                      } else {
                        const { data: clientesEncontrados } = await supabase
                          .from("clientes")
                          .select("id")
                          .ilike("nome", `%${searchLower}%`);
                        const { data: fornecedoresEncontrados } = await supabase
                          .from("fornecedores")
                          .select("id")
                          .ilike("nome", `%${searchLower}%`);
                        const clienteIds = clientesEncontrados?.map((c: any) => c.id) ?? [];
                        const fornecedorIds = fornecedoresEncontrados?.map((f: any) => f.id) ?? [];
                        const orParts = [`status.ilike.%${searchLower}%`, `forma_pagamento.ilike.%${searchLower}%`];
                        if (clienteIds.length > 0) orParts.push(`cliente_id.in.(${clienteIds.join(",")})`);
                        if (fornecedorIds.length > 0) orParts.push(`fornecedor_id.in.(${fornecedorIds.join(",")})`);
                        query = query.or(orParts.join(","));
                      }
                    }

                    const { data, error } = await query.range(offset, offset + pageSize - 1);
                    if (error) throw error;
                    const rows = data || [];
                    if (rows.length === 0) break;
                    all.push(...rows);
                    if (rows.length < pageSize) break;
                    offset += pageSize;
                  }

                  return all;
                }}
              />
              {(filtroTipo === 'saida' || filtroTipo === null) && !somenteLeitura && (
                <Button variant="outline" size="sm" onClick={() => setShowNovaDespesa(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Nova despesa
                </Button>
              )}
            </div>
          </div>
          <div className="mt-4 rounded-md bg-[#C9C9C9] p-3 text-xs">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3 lg:grid-cols-10">
              <div className="lg:col-span-2">
                <Label className="text-xs">Buscar</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input
                    placeholder="ID, pedido, status, forma, cliente..."
                    value={buscaTransacaoInput}
                    onChange={(e) => setBuscaTransacaoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        setBuscaTransacao(buscaTransacaoInput);
                      }
                    }}
                    className="h-8 pl-9 text-xs"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs">Vencimento - início</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.vencInicio}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, vencInicio: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Vencimento - fim</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.vencFim}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, vencFim: e.target.value }))}
                />
              </div>

              <div>
                <Label className="text-xs">Pagamento - início</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.pagInicio}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, pagInicio: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Pagamento - fim</Label>
                <Input
                  type="date"
                  className="h-8 text-xs"
                  value={filtrosDraft.pagFim}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, pagFim: e.target.value }))}
                />
              </div>

              <div>
                <Label className="text-xs">Valor (mín)</Label>
                <Input
                  className="h-8 text-xs"
                  value={filtrosDraft.valorMin}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, valorMin: e.target.value }))}
                  placeholder="Sem limite"
                />
              </div>
              <div>
                <Label className="text-xs">Valor (máx)</Label>
                <Input
                  className="h-8 text-xs"
                  value={filtrosDraft.valorMax}
                  onChange={(e) => setFiltrosDraft((p) => ({ ...p, valorMax: e.target.value }))}
                  placeholder="Sem limite"
                />
              </div>

              <div>
                <Label className="text-xs">Forma de pagamento</Label>
                <Select value={filtrosDraft.formaPagamento} onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, formaPagamento: v }))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {(formasPagamentoDb.length
                      ? formasPagamentoDb
                      : ["PIX", "Boleto", "Cartão", "Transferência", "Dinheiro"]
                    ).map((fp) => (
                      <SelectItem key={fp} value={fp}>
                        {formaPagamentoLabel(fp)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Parcelas</Label>
                <Select value={filtrosDraft.parcelas} onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, parcelas: v as any }))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    <SelectItem value="avista">À vista (1x)</SelectItem>
                    <SelectItem value="parceladas">Parceladas (&gt; 1)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Status</Label>
                <Select value={filtrosDraft.status} onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, status: v as any }))}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                    <SelectItem value="vencido">Vencido</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Estado</Label>
                <Select
                  value={filtrosDraft.estado || "todos"}
                  onValueChange={(v) => {
                    const next = v === "todos" ? "" : v;
                    setFiltrosDraft((p) => ({ ...p, estado: next, cidade: "" }));
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {BRASIL_UFS.map((uf) => (
                      <SelectItem key={uf.sigla} value={uf.sigla}>
                        {uf.sigla} - {uf.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs">Cidade</Label>
                <Select
                  value={filtrosDraft.cidade || "todas"}
                  onValueChange={(v) => setFiltrosDraft((p) => ({ ...p, cidade: v === "todas" ? "" : v }))}
                  disabled={!filtrosDraft.estado}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder={filtrosDraft.estado ? "Todas" : "Selecione um estado"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {cidadesDoEstado.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3 flex justify-end gap-2">
              <Button
                className="h-8 px-3 text-xs"
                variant="secondary"
                onClick={() => {
                  setFiltrosDraft(filtrosDraftDefault);
                  setFiltrosAplicados(filtrosDraftDefault);
                  setBuscaTransacaoInput("");
                  setBuscaTransacao("");
                  setCurrentPage(1);
                }}
              >
                Limpar
              </Button>
              <Button
                className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => {
                  setFiltrosAplicados(filtrosDraft);
                  setCurrentPage(1);
                }}
              >
                Aplicar filtro
              </Button>
            </div>
          </div>
        </CardHeader>
        <NovaDespesaDialog
          open={showNovaDespesa}
          onOpenChange={setShowNovaDespesa}
        />
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                {visibleTransCols.includes("id") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("id")} title="Ordenar">
                    ID{sortIndicator("id")}
                  </TableHead>
                )}
                {visibleTransCols.includes("cliente") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("cliente")} title="Ordenar">
                    Cliente{sortIndicator("cliente")}
                  </TableHead>
                )}
                {filtroTipo === 'entrada' && visibleTransCols.includes("pedido") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("pedido")} title="Ordenar">
                    Pedido{sortIndicator("pedido")}
                  </TableHead>
                )}
                {filtroTipo === 'entrada' && visibleTransCols.includes("parcela") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("parcela")} title="Ordenar">
                    Parcela{sortIndicator("parcela")}
                  </TableHead>
                )}
                {visibleTransCols.includes("tipo") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("tipo")} title="Ordenar">
                    Tipo{sortIndicator("tipo")}
                  </TableHead>
                )}
                {visibleTransCols.includes("valor") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("valor")} title="Ordenar">
                    Valor{sortIndicator("valor")}
                  </TableHead>
                )}
                {visibleTransCols.includes("forma") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("forma")} title="Ordenar">
                    Forma Pagamento{sortIndicator("forma")}
                  </TableHead>
                )}
                {visibleTransCols.includes("status") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("status")} title="Ordenar">
                    Status{sortIndicator("status")}
                  </TableHead>
                )}
                {visibleTransCols.includes("vencimento") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("vencimento")} title="Ordenar">
                    Vencimento{sortIndicator("vencimento")}
                  </TableHead>
                )}
                {visibleTransCols.includes("pagamento") && (
                  <TableHead align="center" className="cursor-pointer select-none" onClick={() => toggleSort("pagamento")} title="Ordenar">
                    Pagamento{sortIndicator("pagamento")}
                  </TableHead>
                )}
                <TableHead align="center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linhas?.map((transacao) => (
                <TableRow key={linhaRecebivelKey(transacao)} className={isTransacaoVencida(transacao) ? 'bg-red-50/70 hover:bg-red-100/70' : ''}>
                  {visibleTransCols.includes("id") && (
                    <TableCell align="center" className="font-medium">
                      <div>{transacao.id}</div>
                      {isReceber && (
                        <div className="text-[10px] font-normal text-muted-foreground">
                          {transacao._origem === "parcela" ? "Contrato" : "Transação"}
                        </div>
                      )}
                    </TableCell>
                  )}
                  {visibleTransCols.includes("cliente") && (
                    <TableCell align="left" className="font-medium">
                    {transacao.fornecedor?.nome ?? transacao.cliente?.nome ?? (transacao.fornecedor_id ? `Fornecedor ID: ${transacao.fornecedor_id}` : transacao.cliente_id ? `Cliente ID: ${transacao.cliente_id}` : "—")}
                    </TableCell>
                  )}
                  {filtroTipo === 'entrada' && visibleTransCols.includes("pedido") && (
                    <TableCell align="center" className="text-gray-700 font-medium">
                      {getPedidoLabel(transacao)}
                    </TableCell>
                  )}
                  {filtroTipo === 'entrada' && visibleTransCols.includes("parcela") && (
                    <TableCell align="center" className="text-gray-600">
                      {getParcelaLabel(transacao) ?? '—'}
                    </TableCell>
                  )}
                  {visibleTransCols.includes("tipo") && (
                    <TableCell align="center">
                    <div className="flex justify-center">
                      <Badge className={getTipoColor(transacao.tipo)}>
                        {transacao.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                      </Badge>
                    </div>
                    </TableCell>
                  )}
                  {visibleTransCols.includes("valor") && (
                    <TableCell align="center" className="font-medium whitespace-nowrap">
                    {formatarValor(transacao.valor)}
                    </TableCell>
                  )}
                  {visibleTransCols.includes("forma") && (
                    <TableCell align="left">
                    {formaPagamentoLabel(transacao.forma_pagamento)}
                    </TableCell>
                  )}
                  {visibleTransCols.includes("status") && (
                    <TableCell align="center">
                    <div className="flex justify-center">
                      <Badge className={getStatusColor(transacao.status, transacao)}>
                        {isTransacaoVencida(transacao) ? 'vencido' : transacao.status}
                      </Badge>
                    </div>
                    </TableCell>
                  )}
                  {visibleTransCols.includes("vencimento") && (
                    <TableCell align="center" className={isTransacaoVencida(transacao) ? 'text-red-700 font-medium' : ''}>
                    {transacao.data_vencimento ? formatarDataLocal(transacao.data_vencimento) : '-'}
                    </TableCell>
                  )}
                  {visibleTransCols.includes("pagamento") && (
                    <TableCell align="center">
                    {transacao.data_pagamento ? formatarDataLocal(transacao.data_pagamento) : '-'}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <div className="flex gap-1 justify-center">
                      {!somenteLeitura && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          if (transacao._parcela) {
                            const parcela = transacao._parcela;
                            if (parcela.numero_parcela === 1) {
                              setParcelaAprovarComComprovante(parcela);
                            } else {
                              marcarParcelaComoPaga.mutateAsync({
                                parcelaId: parcela.id,
                                dataPagamento: new Date().toISOString().split('T')[0],
                              }).then(() => toast.success('Parcela marcada como paga!')).catch((e) => toast.error(e?.message || 'Erro ao marcar parcela'));
                            }
                            return;
                          }
                          if (transacao.tipo === 'entrada') {
                            const ehPrimeiraParcela = transacao.numero_parcela === 1 || !transacao.numero_parcela || (transacao.total_parcelas != null && transacao.total_parcelas <= 1);
                            if (ehPrimeiraParcela) {
                              setTransacaoAprovarComComprovante(transacao);
                            } else {
                              marcarComoPagoOuRecebido(transacao);
                            }
                          } else {
                            marcarComoPagoOuRecebido(transacao);
                          }
                        }}
                        title={transacao._parcela ? 'Marcar como recebido' : transacao.tipo === 'saida' ? 'Marcar como Pago' : 'Marcar como Recebido'}
                        disabled={transacao.status === 'pago'}
                        className={transacao.status === 'pago' ? 'opacity-50' : ''}
                      >
                        <CheckCircle className={`h-4 w-4 ${transacao.status === 'pago' ? 'text-green-600' : ''}`} />
                      </Button>
                      )}
                      {!somenteLeitura && transacao.status === 'pago' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            if (transacao._parcela) {
                              setParcelaParaEstornar(transacao._parcela);
                            } else {
                              setTransacaoParaEstornar(transacao);
                            }
                            setShowEstornoModal(true);
                          }}
                          title="Estornar"
                          className="text-amber-700 border-amber-200 hover:bg-amber-50"
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => visualizarTransacao(transacao)}
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {!somenteLeitura && !transacao._parcela && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => editarTransacao(transacao)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => baixarNotaFiscal(transacao)}
                            title={
                              parseNotasFiscais(transacao).length > 1
                                ? `Baixar ${parseNotasFiscais(transacao).length} comprovantes`
                                : 'Baixar Comprovante'
                            }
                            disabled={!temNotaFiscal(transacao)}
                            className={!temNotaFiscal(transacao) ? 'opacity-50' : ''}
                          >
                            <Download className={`h-4 w-4 ${temNotaFiscal(transacao) ? 'text-green-600' : ''}`} />
                          </Button>
                        </>
                      )}
                      {canDelete() && !somenteLeitura && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (transacao._parcela) {
                              setParcelaParaExcluir(transacao._parcela);
                            } else {
                              setTransacaoParaExcluir(transacao);
                            }
                          }}
                          title={transacao._parcela ? "Excluir parcela" : "Excluir transação"}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {linhas.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {isReceber
                ? "Nenhuma conta a receber encontrada"
                : "Nenhuma transação encontrada"}
            </div>
          )}
          
          {Math.ceil(totalLinhas / itemsPerPage) > 1 && (
            <div className="flex justify-center items-center gap-2 mt-4">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Anterior
              </Button>
              <span className="text-gray-700">
                Página {currentPage} de {Math.ceil(totalLinhas / itemsPerPage)}
              </span>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalLinhas / itemsPerPage), prev + 1))}
                disabled={currentPage >= Math.ceil(totalLinhas / itemsPerPage)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Visualização */}
      <Dialog
        open={showVisualizarModal}
        onOpenChange={(open) => {
          setShowVisualizarModal(open);
          if (!open) {
            setTransacaoSelecionada(null);
            setObservacoesVisualizacao("");
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600 shrink-0" />
              <span>Detalhes da Transação</span>
              {transacaoSelecionada && (
                <>
                  <span className="text-muted-foreground font-normal text-base">
                    #{transacaoSelecionada.id}
                  </span>
                  <Badge className={getStatusColor(transacaoSelecionada.status, transacaoSelecionada)}>
                    {isTransacaoVencida(transacaoSelecionada) ? "vencido" : transacaoSelecionada.status}
                  </Badge>
                  <Badge className={getTipoColor(transacaoSelecionada.tipo)}>
                    {transacaoSelecionada.tipo === "entrada" ? "Entrada" : "Saída"}
                  </Badge>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {transacaoSelecionada && (
            <div className="space-y-4">
              {/* Valor em destaque */}
              <div className="rounded-lg border bg-muted/30 p-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground font-medium">Valor</Label>
                  <div
                    className={`text-2xl font-bold mt-0.5 ${
                      transacaoSelecionada.tipo === "entrada" ? "text-green-700" : "text-red-700"
                    }`}
                  >
                    {formatarValor(transacaoSelecionada.valor)}
                  </div>
                </div>
                <div className="text-right space-y-1">
                  <div>
                    <Label className="text-xs text-muted-foreground font-medium">Forma de pagamento</Label>
                    <div className="text-sm font-medium text-gray-900">
                      {transacaoSelecionada.forma_pagamento || "—"}
                    </div>
                  </div>
                  {transacaoSelecionada.tipo === "entrada" && getParcelaLabel(transacaoSelecionada) && (
                    <div>
                      <Label className="text-xs text-muted-foreground font-medium">Parcela</Label>
                      <div className="text-sm font-medium text-gray-900">
                        {getParcelaLabel(transacaoSelecionada)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Parte / Pedido */}
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium mb-3 flex items-center gap-1.5">
                  <User className="h-4 w-4 text-muted-foreground" />
                  {transacaoSelecionada.tipo === "saida" && transacaoSelecionada.fornecedor_id
                    ? "Fornecedor"
                    : "Cliente"}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="min-w-0">
                    <Label className="text-xs text-muted-foreground font-medium">Nome</Label>
                    <div className="text-sm text-gray-900 mt-0.5 break-words font-medium">
                      {transacaoSelecionada.fornecedor?.nome ??
                        transacaoSelecionada.cliente?.nome ??
                        (transacaoSelecionada.fornecedor_id
                          ? `Fornecedor ID: ${transacaoSelecionada.fornecedor_id}`
                          : transacaoSelecionada.cliente_id
                            ? `Cliente ID: ${transacaoSelecionada.cliente_id}`
                            : "—")}
                    </div>
                    {transacaoSelecionada.cliente?.email && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {transacaoSelecionada.cliente.email}
                      </div>
                    )}
                  </div>
                  {transacaoSelecionada.tipo === "entrada" && getPedidoLabel(transacaoSelecionada) !== "—" && (
                    <div className="min-w-0">
                      <Label className="text-xs text-muted-foreground font-medium">Pedido</Label>
                      <div className="text-sm text-gray-900 mt-0.5 font-semibold">
                        {getPedidoLabel(transacaoSelecionada)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Datas */}
              <div className="rounded-lg border p-4">
                <p className="text-sm font-medium mb-3 flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  Datas
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground font-medium">Vencimento</Label>
                    <div className="text-sm text-gray-900 mt-0.5">
                      {formatarData(transacaoSelecionada.data_vencimento)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground font-medium">Pagamento</Label>
                    <div className="text-sm text-gray-900 mt-0.5">
                      {formatarData(transacaoSelecionada.data_pagamento)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Anexos */}
              {parseNotasFiscais(transacaoSelecionada).length > 0 && (
                <div className="rounded-lg border p-4">
                  <p className="text-sm font-medium mb-3 flex items-center gap-1.5">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    Comprovante
                  </p>
                  <div className="space-y-2">
                    {parseNotasFiscais(transacaoSelecionada).map((nota, index) => (
                      <div
                        key={`${nota.url}-${index}`}
                        className="flex items-center gap-2 p-2.5 rounded-md border bg-background"
                      >
                        {/\.(jpe?g|png|webp|gif)$/i.test(nota.nome) ? (
                          <Image className="h-4 w-4 text-blue-600 shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 text-green-600 shrink-0" />
                        )}
                        <span className="text-sm flex-1 truncate" title={nota.nome}>
                          {nota.nome}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-xs shrink-0"
                          onClick={() => void openStorageUrl(nota.url)}
                        >
                          <Download className="h-3.5 w-3.5 mr-1" />
                          Baixar
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Observações */}
              <div className="rounded-lg border p-4 space-y-2">
                <Label htmlFor="observacoes-visualizacao" className="text-xs text-muted-foreground font-medium">
                  Observações
                </Label>
                <Textarea
                  id="observacoes-visualizacao"
                  value={observacoesVisualizacao}
                  onChange={(e) => setObservacoesVisualizacao(e.target.value)}
                  placeholder="Adicione observações sobre a transação..."
                  rows={3}
                  disabled={somenteLeitura}
                />
                {!somenteLeitura && (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => void salvarObservacoesVisualizacao()}
                      disabled={salvandoObservacoes}
                    >
                      {salvandoObservacoes ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                      ) : (
                        <Save className="h-4 w-4 mr-1.5" />
                      )}
                      Salvar
                    </Button>
                  </div>
                )}
              </div>

              {/* Metadados */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1 border-t">
                <span>ID {transacaoSelecionada.id}</span>
                <span>Criado em {formatarData(transacaoSelecionada.created_at)}</span>
                <span>Atualizado em {formatarData(transacaoSelecionada.updated_at)}</span>
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowVisualizarModal(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog open={showEditarModal} onOpenChange={(open) => { if (!open) fecharModalEdicao(); else setShowEditarModal(true); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-600" />
              Editar Transação
            </DialogTitle>
          </DialogHeader>
          
          {editandoTransacao && (
            <div className="space-y-6">
              {/* Primeira linha - Status e Datas */}
              <div className="grid grid-cols-3 gap-4">
                {/* Status */}
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={editandoTransacao.status} 
                    onValueChange={(value) => setEditandoTransacao({...editandoTransacao, status: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendente">Pendente</SelectItem>
                      <SelectItem value="pago">Pago</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Data de Vencimento */}
                <div className="space-y-2">
                  <Label htmlFor="data_vencimento">Data de Vencimento</Label>
                  <Input
                    id="data_vencimento"
                    type="date"
                    value={editandoTransacao.data_vencimento || ''}
                    onChange={(e) => setEditandoTransacao({...editandoTransacao, data_vencimento: e.target.value})}
                  />
                </div>

                {/* Data de Pagamento */}
                <div className="space-y-2">
                  <Label htmlFor="data_pagamento">Data de Pagamento</Label>
                  <Input
                    id="data_pagamento"
                    type="date"
                    value={editandoTransacao.data_pagamento || ''}
                    onChange={(e) => setEditandoTransacao({...editandoTransacao, data_pagamento: e.target.value})}
                  />
                </div>
              </div>

              {/* Comprovante */}
              <div className="space-y-3">
                <Label>Comprovante</Label>

                {notasFiscaisEdit.length > 0 && (
                  <div className="space-y-2">
                    {notasFiscaisEdit.map((nota, index) => (
                      <div
                        key={`${nota.url}-${index}`}
                        className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800 border border-blue-200"
                      >
                        {/\.(jpe?g|png|webp|gif)$/i.test(nota.nome) ? (
                          <Image className="h-4 w-4 shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0" />
                        )}
                        <span className="flex-1 truncate" title={nota.nome}>{nota.nome}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 shrink-0"
                          title="Remover"
                          onClick={() => setNotasFiscaisEdit((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {notasFiscaisNovas.length > 0 && (
                  <div className="space-y-2">
                    {notasFiscaisNovas.map((file, index) => (
                      <div
                        key={`${file.name}-${index}`}
                        className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-sm text-green-800 border border-green-200"
                      >
                        {file.type.startsWith('image/') ? (
                          <Image className="h-4 w-4 shrink-0" />
                        ) : (
                          <FileText className="h-4 w-4 shrink-0" />
                        )}
                        <span className="flex-1 truncate" title={file.name}>{file.name}</span>
                        <span className="text-xs text-green-600 shrink-0">novo</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-600 shrink-0"
                          title="Remover"
                          onClick={() => setNotasFiscaisNovas((prev) => prev.filter((_, i) => i !== index))}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.pdf"
                    onChange={handleSelecionarNotasFiscais}
                    className="hidden"
                    id="notas-fiscais-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => document.getElementById('notas-fiscais-upload')?.click()}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Selecionar comprovante(s)...
                  </Button>
                </div>
                <p className="text-xs text-gray-500">PDF ou imagem (JPEG, PNG, WebP, GIF). Você pode anexar mais de um comprovante.</p>
              </div>

              {/* Terceira linha - Observações */}
              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={editandoTransacao.observacoes || ''}
                  onChange={(e) => setEditandoTransacao({...editandoTransacao, observacoes: e.target.value})}
                  placeholder="Adicione observações sobre a transação..."
                  rows={4}
                />
              </div>

              {/* Botões */}
              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={salvarEdicao}
                  disabled={atualizarTransacao.isPending}
                  className="flex-1"
                >
                  {atualizarTransacao.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar
                </Button>
                <Button 
                  variant="outline" 
                  onClick={fecharModalEdicao}
                  disabled={atualizarTransacao.isPending}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AprovarComComprovanteDialog
        open={!!transacaoAprovarComComprovante}
        onOpenChange={(open) => { if (!open) setTransacaoAprovarComComprovante(null); }}
        titulo="Aprovar recebimento com comprovante"
        descricao="Anexe o comprovante. Se a venda estiver aguardando confirmação da gerência, o status permanecerá pendente até a aprovação."
        successMessage="Comprovante processado."
        onConfirm={async (file) => {
          if (!transacaoAprovarComComprovante) return;
          const data = await uploadComprovante(file, transacaoAprovarComComprovante.id);
          const aguardando = await pedidoEstaAguardandoConfirmacao(
            transacaoAprovarComComprovante.pedido_id
          );
          if (aguardando) {
            await atualizarTransacao.mutateAsync({
              id: transacaoAprovarComComprovante.id,
              nota_fiscal_url: data.nota_fiscal_url,
              nota_fiscal_name: data.nota_fiscal_name,
            });
            return;
          }
          const dataPagamento = new Date().toISOString().split('T')[0];
          await atualizarTransacao.mutateAsync({
            id: transacaoAprovarComComprovante.id,
            status: 'pago',
            data_pagamento: dataPagamento,
            nota_fiscal_url: data.nota_fiscal_url,
            nota_fiscal_name: data.nota_fiscal_name
          });
        }}
        isPending={atualizarTransacao.isPending}
      />

      <AprovarComComprovanteDialog
        open={!!parcelaAprovarComComprovante}
        onOpenChange={(open) => { if (!open) setParcelaAprovarComComprovante(null); }}
        titulo="Aprovar parcela com comprovante"
        descricao="Anexe o comprovante. Se a venda estiver aguardando confirmação da gerência, o status permanecerá pendente até a aprovação."
        successMessage="Comprovante processado."
        onConfirm={async (file) => {
          if (!parcelaAprovarComComprovante) return;
          const { comprovante_pagamento_url, comprovante_pagamento_name } = await uploadComprovanteParcela(file, parcelaAprovarComComprovante.id);
          const aguardando = await pedidoEstaAguardandoConfirmacao(
            parcelaAprovarComComprovante.pedido_id
          );
          if (aguardando) {
            await marcarParcelaComoPaga.mutateAsync({
              parcelaId: parcelaAprovarComComprovante.id,
              comprovante_pagamento_url,
              comprovante_pagamento_name,
              apenasComprovante: true,
            });
            return;
          }
          await marcarParcelaComoPaga.mutateAsync({
            parcelaId: parcelaAprovarComComprovante.id,
            dataPagamento: new Date().toISOString().split('T')[0],
            comprovante_pagamento_url,
            comprovante_pagamento_name,
          });
        }}
        isPending={marcarParcelaComoPaga.isPending}
      />

      <AlertDialog open={showEstornoModal} onOpenChange={(open) => {
        setShowEstornoModal(open);
        if (!open) {
          setTransacaoParaEstornar(null);
          setParcelaParaEstornar(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{parcelaParaEstornar ? 'Estornar parcela?' : 'Estornar recebimento/pagamento?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {parcelaParaEstornar
                ? 'O status da parcela será alterado para "pendente" e a data de pagamento será removida.'
                : transacaoParaEstornar?.tipo === 'saida'
                  ? ' Esta despesa voltará a constar como conta a pagar.'
                  : ' Este recebimento voltará a constar como pendente.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (parcelaParaEstornar) {
                  estornarParcela(parcelaParaEstornar);
                } else if (transacaoParaEstornar) {
                  estornarTransacao(transacaoParaEstornar);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Estornar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!transacaoParaExcluir}
        onOpenChange={(open) => { if (!open) setTransacaoParaExcluir(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir transação financeira?</AlertDialogTitle>
            <AlertDialogDescription>
              A transação #{transacaoParaExcluir?.id} será removida permanentemente.
              Parcelas vinculadas também serão excluídas. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!transacaoParaExcluir) return;
                excluirTransacao.mutateAsync(transacaoParaExcluir.id)
                  .then(() => {
                    toast.success("Transação excluída com sucesso");
                    setTransacaoParaExcluir(null);
                  })
                  .catch((e) => toast.error(e?.message || "Erro ao excluir transação"));
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!parcelaParaExcluir}
        onOpenChange={(open) => { if (!open) setParcelaParaExcluir(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir parcela?</AlertDialogTitle>
            <AlertDialogDescription>
              A parcela #{parcelaParaExcluir?.id} ({parcelaParaExcluir?.numero_parcela}/
              {parcelaParaExcluir?.total_parcelas}) será removida permanentemente.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (!parcelaParaExcluir) return;
                excluirParcela.mutateAsync(parcelaParaExcluir.id)
                  .then(() => {
                    toast.success("Parcela excluída com sucesso");
                    setParcelaParaExcluir(null);
                  })
                  .catch((e) => toast.error(e?.message || "Erro ao excluir parcela"));
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
} 